import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, Users, Check } from 'lucide-react';

// Standard class list - adjust based on your school's grades
const AVAILABLE_CLASSES = [
  'PS', 'MS', 'GS',                         // Maternelle
  'CP', 'CE1', 'CE2', 'CM1', 'CM2',         // Primaire
  '6 ème', 'EB7(5ème)', 'EB8(4ème)', '3 ème', // Complémentaire
  'S1S', 'S1BF',                            // Secondaire 1
  'S2S', 'S2E', 'S2BF',                     // Secondaire 2
  'S3SG', 'S3SV', 'S3SE', 'S3BF'            // Secondaire 3
];

const ActivityModal = ({
  isOpen,
  onClose,
  onSubmit,
  categories,
  selectedYear,
  availableClasses = AVAILABLE_CLASSES, // Can pass from API if needed
  editActivity = null // Pass this to edit an existing activity
}) => {
  const [formData, setFormData] = useState({
    categoryId: '',
    activityName: '',
    activityType: '',
    paymentSchedule: 'Trimester',
    priceUSD: '',
    startDate: '',
    endDate: '',
    maxCapacity: '',
    description: '',
    allClasses: true,
    selectedClasses: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-fill for edit mode
  useEffect(() => {
    if (editActivity) {
      setFormData({
        categoryId: editActivity.CategoryID?.toString() || '',
        activityName: editActivity.ActivityName || '',
        activityType: editActivity.ActivityType || '',
        paymentSchedule: editActivity.PaymentSchedule || 'Trimester',
        priceUSD: editActivity.PriceUSD?.toString() || '',
        startDate: editActivity.StartDate?.split('T')[0] || '',
        endDate: editActivity.EndDate?.split('T')[0] || '',
        maxCapacity: editActivity.MaxCapacity?.toString() || '',
        description: editActivity.Description || '',
        allClasses: editActivity.AllClasses !== false,
        selectedClasses: editActivity.Classes || []
      });
    }
  }, [editActivity]);

  // Update activity type when category changes
  useEffect(() => {
    if (formData.categoryId) {
      const category = categories.find(c => c.CategoryID === parseInt(formData.categoryId));
      if (category) {
        setFormData(prev => ({ ...prev, activityType: category.ActivityType }));
      }
    }
  }, [formData.categoryId, categories]);

  // Filter categories by selected type
  const filteredCategories = formData.activityType
    ? categories.filter(c => c.ActivityType === formData.activityType)
    : categories;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Toggle class selection
  const toggleClass = (className) => {
    setFormData(prev => {
      const isSelected = prev.selectedClasses.includes(className);
      return {
        ...prev,
        selectedClasses: isSelected
          ? prev.selectedClasses.filter(c => c !== className)
          : [...prev.selectedClasses, className]
      };
    });
  };

  // Select/deselect all classes in a group
  const toggleClassGroup = (classes) => {
    setFormData(prev => {
      const allSelected = classes.every(c => prev.selectedClasses.includes(c));
      if (allSelected) {
        return {
          ...prev,
          selectedClasses: prev.selectedClasses.filter(c => !classes.includes(c))
        };
      } else {
        const newClasses = [...new Set([...prev.selectedClasses, ...classes])];
        return { ...prev, selectedClasses: newClasses };
      }
    });
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.categoryId) newErrors.categoryId = 'Category is required';
    if (!formData.activityName.trim()) newErrors.activityName = 'Activity name is required';
    if (!formData.priceUSD || parseFloat(formData.priceUSD) <= 0) {
      newErrors.priceUSD = 'Valid price is required';
    }
    if (!formData.paymentSchedule) newErrors.paymentSchedule = 'Payment schedule is required';
    if (!formData.allClasses && formData.selectedClasses.length === 0) {
      newErrors.selectedClasses = 'Select at least one class or choose "All Classes"';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setSubmitting(true);
    
    try {
      await onSubmit({
        categoryId: parseInt(formData.categoryId),
        activityName: formData.activityName.trim(),
        activityType: formData.activityType,
        paymentSchedule: formData.paymentSchedule,
        priceUSD: parseFloat(formData.priceUSD),
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        maxCapacity: formData.maxCapacity ? parseInt(formData.maxCapacity) : null,
        description: formData.description.trim() || null,
        allClasses: formData.allClasses,
        applicableClasses: formData.allClasses ? [] : formData.selectedClasses
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Group classes for easier selection
  const classGroups = [
    { name: 'Maternelle', classes: ['PS', 'MS', 'GS'] },
    { name: 'Primaire', classes: ['CP', 'CE1', 'CE2', 'CM1', 'CM2'] },
    { name: 'Complémentaire', classes: ['6 ème', 'EB7(5ème)', 'EB8(4ème)', '3 ème'] },
    { name: 'Secondaire 1', classes: ['S1S', 'S1BF'] },
    { name: 'Secondaire 2', classes: ['S2S', 'S2E', 'S2BF'] },
    { name: 'Secondaire 3', classes: ['S3SG', 'S3SV', 'S3SE', 'S3BF'] }
  ].filter(group => group.classes.some(c => availableClasses.includes(c)));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            {editActivity ? 'Edit Activity' : 'Create New Activity'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Activity Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Activity Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="activityType"
                    value="AfterSchoolSport"
                    checked={formData.activityType === 'AfterSchoolSport'}
                    onChange={(e) => {
                      handleChange('activityType', e.target.value);
                      handleChange('categoryId', '');
                      handleChange('paymentSchedule', 'Trimester');
                    }}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Afterschool Sport
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="activityType"
                    value="SchoolActivity"
                    checked={formData.activityType === 'SchoolActivity'}
                    onChange={(e) => {
                      handleChange('activityType', e.target.value);
                      handleChange('categoryId', '');
                      handleChange('paymentSchedule', 'OneTime');
                    }}
                    className="h-4 w-4 text-purple-600"
                  />
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    School Activity
                  </span>
                </label>
              </div>
            </div>

            {/* Category and Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => handleChange('categoryId', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 ${
                    errors.categoryId ? 'border-red-500' : ''
                  }`}
                >
                  <option value="">Select Category</option>
                  {filteredCategories.map(cat => (
                    <option key={cat.CategoryID} value={cat.CategoryID}>
                      {cat.CategoryName}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.activityName}
                  onChange={(e) => handleChange('activityName', e.target.value)}
                  placeholder="e.g., Football 2024-2025, Spring Outing"
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 ${
                    errors.activityName ? 'border-red-500' : ''
                  }`}
                />
                {errors.activityName && (
                  <p className="text-red-500 text-xs mt-1">{errors.activityName}</p>
                )}
              </div>
            </div>

            {/* Payment and Price */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Schedule <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.paymentSchedule}
                  onChange={(e) => handleChange('paymentSchedule', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 ${
                    errors.paymentSchedule ? 'border-red-500' : ''
                  }`}
                >
                  <option value="Trimester">Trimester (3 payments)</option>
                  <option value="OneTime">One-Time Payment</option>
                  <option value="Monthly">Monthly</option>
                </select>
                {errors.paymentSchedule && (
                  <p className="text-red-500 text-xs mt-1">{errors.paymentSchedule}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DollarSign className="w-4 h-4 inline" /> Total Price (USD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.priceUSD}
                  onChange={(e) => handleChange('priceUSD', e.target.value)}
                  placeholder="0.00"
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 ${
                    errors.priceUSD ? 'border-red-500' : ''
                  }`}
                />
                {errors.priceUSD && (
                  <p className="text-red-500 text-xs mt-1">{errors.priceUSD}</p>
                )}
                {formData.priceUSD && formData.paymentSchedule === 'Trimester' && (
                  <p className="text-xs text-gray-500 mt-1">
                    ${(parseFloat(formData.priceUSD) / 3).toFixed(2)} per trimester
                  </p>
                )}
              </div>
            </div>

            {/* Applicable Classes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline" /> Applicable Classes <span className="text-red-500">*</span>
              </label>
              
              {/* All Classes Toggle */}
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.allClasses}
                  onChange={(e) => handleChange('allClasses', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <span className="font-medium">All Classes</span>
                <span className="text-gray-500 text-sm">(Any student can enroll)</span>
              </label>
              
              {/* Class Selection - Only shown when "All Classes" is unchecked */}
              {!formData.allClasses && (
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  {classGroups.map(group => {
                    const availableInGroup = group.classes.filter(c => availableClasses.includes(c));
                    const allSelected = availableInGroup.every(c => formData.selectedClasses.includes(c));
                    const someSelected = availableInGroup.some(c => formData.selectedClasses.includes(c));
                    
                    return (
                      <div key={group.name}>
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => toggleClassGroup(availableInGroup)}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-medium transition-colors ${
                              allSelected 
                                ? 'bg-blue-100 text-blue-700' 
                                : someSelected 
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {allSelected && <Check className="w-3 h-3" />}
                            {group.name}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 ml-4">
                          {availableInGroup.map(className => {
                            const isSelected = formData.selectedClasses.includes(className);
                            return (
                              <button
                                key={className}
                                type="button"
                                onClick={() => toggleClass(className)}
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                  isSelected
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:border-blue-500'
                                }`}
                              >
                                {className}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  
                  {formData.selectedClasses.length > 0 && (
                    <div className="pt-2 border-t mt-3">
                      <span className="text-sm text-gray-600">
                        Selected: <strong>{formData.selectedClasses.sort().join(', ')}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}
              
              {errors.selectedClasses && (
                <p className="text-red-500 text-xs mt-1">{errors.selectedClasses}</p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline" /> Start Date
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline" /> End Date
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Capacity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users className="w-4 h-4 inline" /> Max Capacity
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxCapacity}
                  onChange={(e) => handleChange('maxCapacity', e.target.value)}
                  placeholder="Leave empty for unlimited"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional: Limit enrollment to this number
                </p>
              </div>
              <div></div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                placeholder="Optional description of this activity..."
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Academic Year Info */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              This activity will be created for academic year: <strong>{selectedYear}</strong>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Saving...' : editActivity ? 'Update Activity' : 'Create Activity'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityModal;